// Map module using MapLibre GL

import maplibregl from 'maplibre-gl';
import MapboxDraw from 'maplibre-gl-draw';
import 'maplibre-gl-draw/dist/mapbox-gl-draw.css';
import { getLayer } from './modules/layerManager.js';

let map = null;
let popup = null;
let highlightLayer = null;
let mapLayers = {}; // Store references to map layers
let draw = null; // Draw control instance
let currentHighlightedFeature = null; // Track currently highlighted feature
let editMode = false; // Track if edit mode is active
let currentEditLayerId = null; // Track which layer is being edited
let currentEditFeatureIndex = null; // Track the index of the feature being edited
let editHistory = []; // History of geometry states for undo
let editHistoryIndex = -1; // Current position in history
let isUndoRedoAction = false; // Flag to prevent history tracking during undo/redo
let vertexHoverHandler = null; // Track vertex hover handler for cleanup
let hoveredVertexCoords = null; // Track currently hovered vertex coordinates
let featureSelectedForEdit = null; // Track feature selected but not yet in vertex edit mode
let verticesVisible = false; // Track whether vertices should be visible
let activeDrawingMode = null; // Track the active drawing mode for continuous drawing

/**
 * Check if currently in drawing mode
 */
export function isDrawingMode() {
    return activeDrawingMode !== null;
}

/**
 * Update draw button visual state
 */
export function updateDrawButtonState(isActive) {
    const legendDrawBtn = document.getElementById('legend-draw-btn');
    if (!legendDrawBtn) return;

    const icon = legendDrawBtn.querySelector('svg');

    if (isActive) {
        // Highlight icon, text, and border (but not background)
        if (icon) {
            icon.style.stroke = 'var(--color-primary)';
        }
        legendDrawBtn.style.borderColor = 'var(--color-primary)';
        legendDrawBtn.style.color = 'var(--color-primary)';
        legendDrawBtn.title = 'Stop drawing';
    } else {
        // Reset to defaults
        if (icon) {
            icon.style.stroke = '';
        }
        legendDrawBtn.style.borderColor = '';
        legendDrawBtn.style.color = '';
        legendDrawBtn.title = 'Draw new feature';
    }
}

/**
 * Custom styles for the draw control
 * Makes vertices clearly visible and uses distinct colors
 */
function getDrawStyles() {
    return [
        // IMPORTANT: Hide any polygon fill when in line drawing mode
        {
            'id': 'gl-draw-polygon-fill-line-mode',
            'type': 'fill',
            'filter': ['all',
                ['==', 'mode', 'draw_line_string']
            ],
            'paint': {
                'fill-opacity': 0
            }
        },
        // Hide polygon stroke when in line drawing mode
        {
            'id': 'gl-draw-polygon-stroke-line-mode',
            'type': 'line',
            'filter': ['all',
                ['==', 'mode', 'draw_line_string'],
                ['==', '$type', 'Polygon']
            ],
            'paint': {
                'line-opacity': 0
            }
        },
        // Polygon fill (inactive)
        {
            'id': 'gl-draw-polygon-fill-inactive',
            'type': 'fill',
            'filter': ['all',
                ['==', 'active', 'false'],
                ['==', '$type', 'Polygon'],
                ['!=', 'mode', 'static']
            ],
            'paint': {
                'fill-color': 'rgba(75, 192, 192, 0.1)',
                'fill-outline-color': 'rgba(75, 192, 192, 1)',
                'fill-opacity': 0.1
            }
        },
        // Polygon fill (active) - highlighted when clicked
        {
            'id': 'gl-draw-polygon-fill-active',
            'type': 'fill',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'paint': {
                'fill-color': 'rgba(75, 192, 192, 0.25)',
                'fill-outline-color': 'rgba(75, 192, 192, 1)',
                'fill-opacity': 0.25
            }
        },
        // Polygon outline (inactive)
        {
            'id': 'gl-draw-polygon-stroke-inactive',
            'type': 'line',
            'filter': ['all',
                ['==', 'active', 'false'],
                ['==', '$type', 'Polygon'],
                ['!=', 'mode', 'static']
            ],
            'layout': {
                'line-cap': 'round',
                'line-join': 'round'
            },
            'paint': {
                'line-color': 'rgba(75, 192, 192, 0.6)',
                'line-width': 2,
                'line-blur': 0
            }
        },
        // Polygon outline (active) - highlighted when clicked
        {
            'id': 'gl-draw-polygon-stroke-active',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'layout': {
                'line-cap': 'round',
                'line-join': 'round'
            },
            'paint': {
                'line-color': 'rgba(75, 192, 192, 1)',
                'line-width': 3,
                'line-blur': 0
            }
        },
        // Line (inactive)
        {
            'id': 'gl-draw-line-inactive',
            'type': 'line',
            'filter': ['all',
                ['==', 'active', 'false'],
                ['==', '$type', 'LineString'],
                ['!=', 'mode', 'static']
            ],
            'layout': {
                'line-cap': 'round',
                'line-join': 'round'
            },
            'paint': {
                'line-color': 'rgba(75, 192, 192, 0.6)',
                'line-width': 3,
                'line-blur': 0
            }
        },
        // Line (active) - highlighted when clicked (can move/delete whole road)
        {
            'id': 'gl-draw-line-active',
            'type': 'line',
            'filter': ['all',
                ['==', '$type', 'LineString'],
                ['==', 'active', 'true']
            ],
            'layout': {
                'line-cap': 'round',
                'line-join': 'round'
            },
            'paint': {
                'line-color': 'rgba(75, 192, 192, 1)',
                'line-width': 4,
                'line-blur': 0
            }
        },
        // Line being drawn (draw_line_string mode)
        {
            'id': 'gl-draw-line-drawing',
            'type': 'line',
            'filter': ['all',
                ['==', 'mode', 'draw_line_string']
            ],
            'layout': {
                'line-cap': 'round',
                'line-join': 'round'
            },
            'paint': {
                'line-color': 'rgba(75, 192, 192, 1)',
                'line-width': 3
            }
        },
        // Point (inactive)
        {
            'id': 'gl-draw-point-inactive',
            'type': 'circle',
            'filter': ['all',
                ['==', 'active', 'false'],
                ['==', '$type', 'Point'],
                ['!=', 'mode', 'static']
            ],
            'paint': {
                'circle-radius': 6,
                'circle-color': 'rgba(75, 192, 192, 0.6)',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
                'circle-blur': 0
            }
        },
        // Point (active) - highlighted when clicked
        {
            'id': 'gl-draw-point-active',
            'type': 'circle',
            'filter': ['all',
                ['==', '$type', 'Point'],
                ['==', 'active', 'true']
            ],
            'paint': {
                'circle-radius': 8,
                'circle-color': 'rgba(75, 192, 192, 1)',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-blur': 0
            }
        },
        // Vertex points - INVISIBLE by default (only show on hover)
        {
            'id': 'gl-draw-polygon-and-line-vertex-active',
            'type': 'circle',
            'filter': ['all',
                ['==', 'meta', 'vertex'],
                ['==', '$type', 'Point']
            ],
            'paint': {
                'circle-radius': 0,
                'circle-color': 'rgba(45, 140, 140, 0)',
                'circle-opacity': 0,
                'circle-blur': 0
            }
        },
        // Halo around vertices - invisible
        {
            'id': 'gl-draw-polygon-and-line-vertex-halo-active',
            'type': 'circle',
            'filter': ['all',
                ['==', 'meta', 'vertex'],
                ['==', '$type', 'Point']
            ],
            'paint': {
                'circle-radius': 0,
                'circle-color': 'rgba(45, 140, 140, 0)',
                'circle-opacity': 0,
                'circle-blur': 0
            }
        },
        // Midpoint vertices - dark gray (for adding new vertices)
        {
            'id': 'gl-draw-polygon-midpoint',
            'type': 'circle',
            'filter': ['all',
                ['==', 'meta', 'midpoint'],
                ['==', '$type', 'Point']
            ],
            'paint': {
                'circle-radius': 4,
                'circle-color': '#666666',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1,
                'circle-opacity': 0.9,
                'circle-blur': 0
            }
        }
    ];
}

/**
 * Initialize vertex and feature hover highlight layers
 * Adds sources and layers to show highlights over hovered vertices and movable features
 */
function initVertexHoverHighlight() {
    if (!map) return;

    // Add source for vertex hover highlight
    if (!map.getSource('vertex-hover-source')) {
        map.addSource('vertex-hover-source', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });
    }

    // Add outer glow for vertex hover - shows where vertex is
    if (!map.getLayer('vertex-hover-highlight-outer')) {
        map.addLayer({
            id: 'vertex-hover-highlight-outer',
            type: 'circle',
            source: 'vertex-hover-source',
            paint: {
                'circle-radius': 12,
                'circle-color': 'rgba(75, 192, 192, 0.4)',
                'circle-opacity': 0.6,
                'circle-blur': 0.8
            }
        });
    }

    // Add middle circle for vertex hover
    if (!map.getLayer('vertex-hover-highlight')) {
        map.addLayer({
            id: 'vertex-hover-highlight',
            type: 'circle',
            source: 'vertex-hover-source',
            paint: {
                'circle-radius': 7,
                'circle-color': 'rgba(75, 192, 192, 1)',
                'circle-opacity': 1,
                'circle-blur': 0,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2
            }
        });
    }

    // Add source for feature hover highlight (when feature is movable)
    if (!map.getSource('feature-hover-source')) {
        map.addSource('feature-hover-source', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });
    }

    // Add layer for feature hover highlight - line - teal highlight for movable features
    if (!map.getLayer('feature-hover-highlight-line')) {
        map.addLayer({
            id: 'feature-hover-highlight-line',
            type: 'line',
            source: 'feature-hover-source',
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-color': 'rgba(75, 192, 192, 0.8)',
                'line-width': 5,
                'line-opacity': 0.8,
                'line-blur': 0
            }
        });
    }

    // Add layer for feature hover highlight - polygon fill - teal highlight for movable features
    if (!map.getLayer('feature-hover-highlight-fill')) {
        map.addLayer({
            id: 'feature-hover-highlight-fill',
            type: 'fill',
            source: 'feature-hover-source',
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'fill-color': 'rgba(75, 192, 192, 0.3)',
                'fill-opacity': 0.3
            }
        });
    }

    // Add layer for feature hover highlight - polygon outline - teal highlight for movable features
    if (!map.getLayer('feature-hover-highlight-outline')) {
        map.addLayer({
            id: 'feature-hover-highlight-outline',
            type: 'line',
            source: 'feature-hover-source',
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'line-color': 'rgba(75, 192, 192, 1)',
                'line-width': 4,
                'line-opacity': 1,
                'line-blur': 0
            }
        });
    }

    // Add layer for feature hover highlight - point - teal highlight for movable features
    if (!map.getLayer('feature-hover-highlight-point')) {
        map.addLayer({
            id: 'feature-hover-highlight-point',
            type: 'circle',
            source: 'feature-hover-source',
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-radius': 12,
                'circle-color': 'rgba(75, 192, 192, 1)',
                'circle-opacity': 0.8,
                'circle-blur': 0,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2
            }
        });
    }
}

/**
 * Initialize the map
 * @param {string} containerId - The ID of the container element
 * @returns {maplibregl.Map} The map instance
 */
export function initMap(containerId) {
    map = new maplibregl.Map({
        container: containerId,
        style: {
            version: 8,
            sources: {
                'carto-dark': {
                    type: 'raster',
                    tiles: [
                        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
                    ],
                    tileSize: 256,
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                }
            },
            layers: [
                {
                    id: 'carto-dark',
                    type: 'raster',
                    source: 'carto-dark',
                    minzoom: 0,
                    maxzoom: 19
                },
                {
                    id: 'dark-overlay',
                    type: 'background',
                    paint: {
                        'background-color': 'rgba(0, 0, 0, 0.0)'
                    }
                }
            ]
        },
        center: [-5, 25],
        zoom: 1,
        attributionControl: { compact: true }
    });

    // Add navigation controls
    map.addControl(new maplibregl.NavigationControl(), 'top-right');

    // Add scale control
    map.addControl(new maplibregl.ScaleControl({
        maxWidth: 200,
        unit: 'metric'
    }), 'bottom-left');

    // Force attribution to be closed on load
    map.on('load', () => {
        const attribControl = document.querySelector('.maplibregl-ctrl-attrib');
        if (attribControl) {
            attribControl.classList.remove('maplibregl-compact-show');
        }
    });

    // Initialize popup
    popup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: false,
        offset: 15,
        maxWidth: '280px',
        className: 'feature-popup'
    });

    // Initialize Draw control (but don't add to map yet)
    // It will be added only when entering edit mode to prevent click interception
    draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
            trash: true
        },
        defaultMode: 'simple_select',
        styles: getDrawStyles(),
        // Enable proper cursor states
        clickBuffer: 2,
        touchBuffer: 25,
        boxSelect: false,
        touchEnabled: true,
        keybindings: true
    });

    // NOTE: Draw control is NOT added to map here
    // It's added dynamically when entering edit mode (see enterEditMode())
    // This prevents it from intercepting clicks in normal view mode

    // Listen for draw events
    map.on('draw.create', handleGeometryCreate);
    map.on('draw.update', handleGeometryUpdate);
    map.on('draw.delete', handleGeometryDelete);

    // Track geometry changes for undo/redo
    map.on('draw.update', trackGeometryChange);
    map.on('draw.delete', trackGeometryDelete);
    map.on('draw.selectionchange', handleSelectionChange);

    // Sync button state with draw control mode changes
    map.on('draw.modechange', (e) => {
        // Only update button if we're tracking an active drawing mode
        if (activeDrawingMode) {
            updateDrawButtonState(true);
        }
    });

    // Initialize vertex hover highlight layer after map style loads
    if (map.isStyleLoaded()) {
        initVertexHoverHighlight();
    } else {
        map.once('load', () => {
            initVertexHoverHighlight();
        });
    }

// Handle switching between vertex and feature selection in edit mode
map.on('click', (e) => {
    if (editMode && draw) {
        const mode = draw.getMode();

        // If in direct_select (editing vertices) and user clicks the line (not a vertex)
        if (mode === 'direct_select') {
            const features = map.queryRenderedFeatures(e.point);
            const clickedFeature = features.find(f =>
                f.source && f.source.includes('mapbox-gl-draw') &&
                f.properties && !f.properties.meta
            );

            // If clicked on the feature line (not a vertex or midpoint)
            if (clickedFeature) {
                const selected = draw.getSelected();
                if (selected && selected.features && selected.features.length > 0) {
                    const featureId = selected.features[0].id;
                    // Switch to simple_select to select whole feature
                    draw.changeMode('simple_select', { featureIds: [featureId] });
                    console.log('Switched to simple_select - whole feature selected');
                    return;
                }
            }
        }
    }
});

// Handle map clicks for feature selection, highlight, and clearing
map.on('click', (e) => {
    const features = map.queryRenderedFeatures(e.point);

    // ========== EDIT MODE: let existing logic handle selection ==========
    if (editMode && currentEditLayerId) {
        // Check if we clicked on a draw feature (vertices, midpoints, or the feature being edited)
        const clickedDrawFeature = features.some(f => f.source && f.source.startsWith('mapbox-gl-draw'));

        if (!clickedDrawFeature) {
            // Find features from the current edit layer
            const sourceId = `${currentEditLayerId}-source`;
            const layerFeatures = features.filter(f =>
                f.source === sourceId ||
                f.sourceLayer === sourceId ||
                (f.layer && (f.layer.id === currentEditLayerId || f.layer.id.startsWith(currentEditLayerId)))
            );

            if (layerFeatures.length > 0) {
                const feature = layerFeatures[0];
                if (feature.properties.__featureIndex !== undefined) {
                    const featureIndex = feature.properties.__featureIndex;
                    const source = map.getSource(sourceId);

                    if (source && source._data && source._data.features) {
                        const originalFeature = source._data.features[featureIndex];
                        if (originalFeature) {
                            loadSingleFeatureForEditing(originalFeature, currentEditLayerId);
                        }
                    }
                }
            }
        }

        return; // don’t do normal highlight logic in edit mode
    }

    const nonDrawFeatures = features.filter(
        f =>
            f.source &&
            !f.source.startsWith('mapbox-gl-draw') &&
            f.layer &&
            (
                f.layer.id in mapLayers ||
                Object.values(mapLayers).some(L =>
                    L.fillLayerId === f.layer.id ||
                    L.layerId === f.layer.id
                )
            )
    );


    if (nonDrawFeatures.length > 0) {
        const feature = nonDrawFeatures[0];

        let layerName = 'Layer';
        let layerIdForEvent = null;

        const layerEntry = Object.values(mapLayers).find(entry => entry.sourceId === feature.source);
        if (layerEntry) {
            layerName = layerEntry.layerName || 'Layer';
            layerIdForEvent = layerEntry.layerId || null;
        }

        showFeaturePopup(feature, e.lngLat, layerName);

        const featureIndex = feature.properties && feature.properties.__featureIndex;

        if (Number.isInteger(featureIndex)) {
            document.dispatchEvent(
                new CustomEvent('mapFeatureClicked', {
                    detail: {
                        featureIndex,
                        layerId: layerIdForEvent
                    }
                })
            );
        } else {
            highlightFeature(feature, true);
        }
    } else {
        removeHighlight();
        document.dispatchEvent(new CustomEvent('clearHighlight'));
    }
});

    document.addEventListener('clearHighlight', () => {
        removeHighlight();
    });

    // Listen for requests to load a feature for editing (from table clicks in edit mode)
    document.addEventListener('loadFeatureForEdit', (e) => {
        const { feature, shouldZoom } = e.detail;
        if (feature && editMode) {
            loadSingleFeatureForEditing(feature);

            // Zoom to the feature if requested
            if (shouldZoom) {
                zoomToFeature(feature);
            }
        }
    });

    return map;
}

/**
 * Handle geometry creation from draw control
 */
function handleGeometryCreate(e) {
    if (!e.features || e.features.length === 0) return;

    console.log(`[DRAW CREATE] ${e.features.length} feature(s) created`);

    // Dispatch event for each created feature to save it immediately
    e.features.forEach(feature => {
        const event = new CustomEvent('geometryCreated', {
            detail: {
                layerId: getCurrentEditLayerId(),
                geometry: feature.geometry,
                properties: feature.properties || {}
            }
        });
        document.dispatchEvent(event);
    });

    // Re-enter drawing mode to allow immediately drawing another feature
    // Use requestAnimationFrame to ensure draw control has completed its internal state transitions
    if (activeDrawingMode) {
        const modeToReenter = activeDrawingMode;
        requestAnimationFrame(() => {
            if (draw && activeDrawingMode === modeToReenter) {
                draw.changeMode(modeToReenter);

                // Ensure cursor is set correctly
                if (map && map.getCanvas()) {
                    map.getCanvas().style.cursor = 'crosshair';
                }

                console.log(`[DRAW] Re-entered ${modeToReenter} mode for continuous drawing`);
            }
        });
    }
}

/**
 * Handle geometry updates from draw control
 */
function handleGeometryUpdate(e) {
    if (!e.features || e.features.length === 0) return;

    // Dispatch event for each updated feature
    e.features.forEach(feature => {
        if (feature.properties && feature.properties.__featureIndex !== undefined) {
            const event = new CustomEvent('geometryUpdated', {
                detail: {
                    featureIndex: feature.properties.__featureIndex,
                    geometry: feature.geometry
                }
            });
            document.dispatchEvent(event);
        }
    });
}

/**
 * Track geometry changes for undo/redo
 */
function trackGeometryChange(e) {
    // Don't track changes during undo/redo operations
    if (isUndoRedoAction) return;

    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];
    if (!feature) return;

    // Save the current state to history
    const geometryCopy = JSON.parse(JSON.stringify(feature.geometry));

    // Remove any "future" history if we're not at the end
    if (editHistoryIndex < editHistory.length - 1) {
        editHistory = editHistory.slice(0, editHistoryIndex + 1);
    }

    // Add new state to history
    editHistory.push({
        featureId: feature.id,
        geometry: geometryCopy,
        properties: { ...feature.properties }
    });

    editHistoryIndex = editHistory.length - 1;

    // Update undo button state
    updateUndoButtonState();

    console.log(`History saved (${editHistory.length} states)`);
}

/**
 * Track geometry deletions for undo/redo
 */
function trackGeometryDelete(e) {
    // Don't track changes during undo/redo operations
    if (isUndoRedoAction) return;

    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];

    // Save the deleted feature state to history
    const geometryCopy = JSON.parse(JSON.stringify(feature.geometry));

    // Remove any "future" history if we're not at the end
    if (editHistoryIndex < editHistory.length - 1) {
        editHistory = editHistory.slice(0, editHistoryIndex + 1);
    }

    // Add deletion state to history
    editHistory.push({
        featureId: feature.id,
        geometry: geometryCopy,
        properties: { ...feature.properties },
        action: 'delete' // Mark as deletion
    });

    editHistoryIndex = editHistory.length - 1;

    // Update undo button state
    updateUndoButtonState();

    console.log(`Delete tracked in history (${editHistory.length} states)`);
}

/**
 * Handle selection changes to initialize history
 */
function handleSelectionChange(e) {
    if (!e.features || e.features.length === 0) {
        return;
    }

    const feature = e.features[0];
    if (!feature) return;

    // Initialize history with the original state when a feature is first selected
    if (editHistory.length === 0) {
        const geometryCopy = JSON.parse(JSON.stringify(feature.geometry));
        editHistory = [{
            featureId: feature.id,
            geometry: geometryCopy,
            properties: { ...feature.properties }
        }];
        editHistoryIndex = 0;
        updateUndoButtonState();
        console.log('Edit history initialized');
    }
}

/**
 * Handle geometry deletion from draw control
 */
function handleGeometryDelete(e) {
    if (!e.features || e.features.length === 0) return;

    // Dispatch event for each deleted feature
    e.features.forEach(feature => {
        if (feature.properties && feature.properties.__featureIndex !== undefined) {
            const event = new CustomEvent('geometryDeleted', {
                detail: {
                    featureIndex: feature.properties.__featureIndex
                }
            });
            document.dispatchEvent(event);
        }
    });
}

export function addGeoJSONToMap(geojson, options = {}) {
    if (!map) {
        console.error('Map not initialized');
        return;
    }

    const layerId = options.layerId || 'shapefile-layer';
    const sourceId = `${layerId}-source`;
    const color = options.color || '#667eea';
    const layerName = options.layerName ? options.layerName.replace(/\.zip$/i, '') : 'Layer';

    // 🔹 Add a stable __featureIndex to every feature
    const geojsonWithIndex = {
        ...geojson,
        features: (geojson.features || []).map((feature, index) => ({
            ...feature,
            properties: {
                ...(feature.properties || {}),
                __featureIndex: index
            }
        }))
    };

    // Determine geometry type from first feature, or from metadata if provided
    const firstFeature = geojsonWithIndex.features?.[0];
    let geometryType = firstFeature?.geometry?.type;

    // If no features, try to get geometry type from metadata
    if (!geometryType && options.metadata && options.metadata.geometryType) {
        geometryType = options.metadata.geometryType;
        console.log(`Using geometry type from metadata: ${geometryType}`);
    }

    // Add / update source
    if (map.getSource(sourceId)) {
        map.getSource(sourceId).setData(geojsonWithIndex);
    } else {
        map.addSource(sourceId, {
            type: 'geojson',
            data: geojsonWithIndex
        });
    }

    let fillLayerId = null;

    // =============== POINTS ===============
    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
        if (!map.getLayer(layerId)) {
            map.addLayer({
                id: layerId,
                type: 'circle',
                source: sourceId,
                paint: {
                    'circle-radius': 6,
                    'circle-color': color,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff'
                }
            });
        }
        fillLayerId = null;
    }

    // =============== LINES ===============
    else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
        if (!map.getLayer(layerId)) {
            map.addLayer({
                id: layerId,
                type: 'line',
                source: sourceId,
                paint: {
                    'line-color': color,
                    'line-width': 3
                }
            });
        }
        fillLayerId = null;
    }

    // =============== POLYGONS ===============
    else {
        // ✅ Use a single FILL layer with fill-outline-color, so the WHOLE polygon is clickable
        if (!map.getLayer(layerId)) {
            map.addLayer({
                id: layerId,
                type: 'fill',
                source: sourceId,
                paint: {
                    'fill-color': color,
                    'fill-opacity': 0.3,
                    'fill-outline-color': color
                }
            });
        }
        fillLayerId = layerId; // for consistency
    }

    // Store layer reference
    mapLayers[layerId] = {
        layerId,
        sourceId,
        fillLayerId,
        layerName,
        color
    };

    // ---------- CLICK HANDLER ----------
    const clickHandler = (e) => {
        const feature = e.features && e.features[0];
        if (!feature) return;

        // In edit mode: let edit logic handle things, do not highlight
        if (editMode) return;

        // 1) Popup
        showFeaturePopup(feature, e.lngLat, layerName);

        // 2) Map highlight (works anywhere on polygon, not just edge)
        highlightFeature(feature, false);

        // 3) Notify table
        const idx = feature.properties && feature.properties.__featureIndex;
        if (Number.isInteger(idx) && typeof options.onFeatureClick === 'function') {
            options.onFeatureClick(idx);
        }
    };

    // Remove any old handlers to avoid duplicates
    map.off('click', layerId, clickHandler);

    // Attach click handler:
    //  - points: circle layer (layerId)
    //  - lines: line layer (layerId)
    //  - polygons: fill layer (layerId)
    map.on('click', layerId, clickHandler);

    // ---------- CURSOR ----------
    const mouseEnterHandler = () => {
        if (!editMode) {
            map.getCanvas().style.cursor = 'pointer';
        }
    };
    const mouseLeaveHandler = () => {
        if (!editMode) {
            map.getCanvas().style.cursor = '';
        }
    };

    map.off('mouseenter', layerId, mouseEnterHandler);
    map.off('mouseleave', layerId, mouseLeaveHandler);

    map.on('mouseenter', layerId, mouseEnterHandler);
    map.on('mouseleave', layerId, mouseLeaveHandler);

    // ---------- FIT TO BOUNDS ----------
    if (!options.skipFitBounds) {
        fitMapToBounds(geojsonWithIndex);
    }
}


export function removeLayerFromMap(layerId) {
    if (!map || !mapLayers[layerId]) return;

    const layerInfo = mapLayers[layerId];

    if (layerInfo.fillLayerId && map.getLayer(layerInfo.fillLayerId)) {
        map.removeLayer(layerInfo.fillLayerId);
    }

    if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
    }

    if (map.getSource(layerInfo.sourceId)) {
        map.removeSource(layerInfo.sourceId);
    }

    delete mapLayers[layerId];

    popup.remove();
}

export function setLayerVisibility(layerId, visible) {
    if (!map || !mapLayers[layerId]) return;

    const layerInfo = mapLayers[layerId];
    const visibility = visible ? 'visible' : 'none';

    if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
    }

    if (layerInfo.fillLayerId && map.getLayer(layerInfo.fillLayerId)) {
        map.setLayoutProperty(layerInfo.fillLayerId, 'visibility', visibility);
    }
}

/**
 * Fit map to GeoJSON bounds
 * @param {Object} geojson - The GeoJSON data
 */
function fitMapToBounds(geojson) {
    if (!geojson.features || geojson.features.length === 0) return;

    const coords = [];

    geojson.features.forEach(feature => {
        extractAllCoordinates(feature.geometry, coords);
    });

    if (coords.length === 0) return;

    // Use reduce to avoid stack overflow with large datasets
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    coords.forEach(coord => {
        if (coord && coord.length >= 2) {
            const lng = coord[0];
            const lat = coord[1];
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    });

    const bounds = [
        [minLng, minLat], // Southwest
        [maxLng, maxLat]  // Northeast
    ];

    // Calculate padding to account for the attribute table
    // The table takes up approximately 33% of the viewport height
    const viewportHeight = window.innerHeight;
    const tableHeight = Math.round(viewportHeight * 0.33); // 33% for table

    map.fitBounds(bounds, {
        padding: {
            top: 50,
            bottom: tableHeight + 50, // Extra padding for table + margin
            left: 50,
            right: 50
        },
        maxZoom: 16
    });
}

/**
 * Extract all coordinates from geometry
 * @param {Object} geometry - The geometry object
 * @param {Array} coords - Array to collect coordinates
 */
function extractAllCoordinates(geometry, coords) {
    if (!geometry) return;

    const { type, coordinates } = geometry;

    switch (type) {
        case 'Point':
            coords.push(coordinates);
            break;
        case 'MultiPoint':
        case 'LineString':
            coords.push(...coordinates);
            break;
        case 'MultiLineString':
        case 'Polygon':
            coordinates.forEach(ring => coords.push(...ring));
            break;
        case 'MultiPolygon':
            coordinates.forEach(polygon => {
                polygon.forEach(ring => coords.push(...ring));
            });
            break;
        case 'GeometryCollection':
            geometry.geometries.forEach(g => extractAllCoordinates(g, coords));
            break;
    }
}

/**
 * Show popup with feature properties
 * @param {Object} feature - The feature
 * @param {Object} lngLat - The coordinates
 * @param {string} layerName - The layer name
 */
function showFeaturePopup(feature, lngLat, layerName = 'Layer') {
    // Don't show popup in edit mode
    if (editMode) {
        return;
    }

    const properties = feature.properties || {};
    console.log('Popup - Feature properties:', properties);

    // Filter out internal properties
    const propertyKeys = Object.keys(properties).filter(key => !key.startsWith('__'));
    console.log('Popup - Display keys:', propertyKeys);

    if (propertyKeys.length === 0) {
        popup
            .setLngLat(lngLat)
            .setHTML('<div class="popup-empty">No attributes</div>')
            .addTo(map);
        return;
    }

    // Truncate layer name if too long
    const maxLayerNameLength = 30;
    const displayName = layerName.length > maxLayerNameLength
        ? layerName.substring(0, maxLayerNameLength) + '...'
        : layerName;

    // Check if there's a thumbnail to display
    const thumbnailKey = propertyKeys.find(key =>
        key.toLowerCase() === 'thumbnail' &&
        typeof properties[key] === 'string' &&
        properties[key].startsWith('data:image/')
    );

    const thumbnailHTML = thumbnailKey ? `
        <div class="popup-image">
            <img src="${properties[thumbnailKey]}"
                 style="width: 100%; max-height: 150px; object-fit: cover; border-radius: 6px; margin-bottom: 8px;"
                 alt="Photo thumbnail" />
        </div>
    ` : '';

    // Filter out thumbnail from text display
    const displayKeys = propertyKeys.filter(key => key !== thumbnailKey);

    const tableRows = displayKeys.map(key => {
        const value = properties[key];

        // Skip image data URLs in text display
        if (typeof value === 'string' && value.startsWith('data:image/')) {
            return '';
        }

        const displayValue = value !== null && value !== undefined ? String(value) : '<em style="color: #bdc3c7;">null</em>';

        // Truncate long values
        const truncatedValue = String(value).length > 50
            ? String(value).substring(0, 50) + '...'
            : displayValue;

        return `
            <tr>
                <td title="${key}">${key}:</td>
                <td title="${value !== null && value !== undefined ? String(value) : 'null'}">${truncatedValue}</td>
            </tr>
        `;
    }).filter(row => row !== '').join('');

    console.log('Popup - Table rows HTML length:', tableRows.length);

    const html = `
        <div class="popup-container">
            <div class="popup-header">
                <div class="popup-title" title="${layerName}">${displayName}</div>
            </div>
            <div class="popup-content">
                ${thumbnailHTML}
                <table class="popup-table">
                    ${tableRows}
                </table>
            </div>
        </div>
    `;

    console.log('Popup - Full HTML length:', html.length);
    console.log('Popup - HTML preview:', html.substring(0, 200));

    popup
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(map);
}

/**
 * Get the map instance
 * @returns {maplibregl.Map}
 */
export function getMap() {
    return map;
}

/**
 * Resize the map (useful when container size changes)
 */
export function resizeMap() {
    if (map) {
        map.resize();
    }
}

/**
 * Set up cursor feedback for point features in simple_select mode
 * Shows feature highlight and move cursor when hovering over a point
 */
function setupPointCursorFeedback() {
    if (!map || !draw) return;

    // Remove previous handler if it exists
    if (vertexHoverHandler) {
        map.off('mousemove', vertexHoverHandler);
    }

    // Create new handler for point features
    vertexHoverHandler = (e) => {
        // Only apply in simple_select mode for point features
        if (!editMode || draw.getMode() !== 'simple_select') {
            return;
        }

        // Query features at mouse point
        const features = map.queryRenderedFeatures(e.point);

        // Find active point feature
        const pointFeature = features.find(f =>
            f.geometry &&
            f.geometry.type === 'Point' &&
            f.properties &&
            f.properties.active === 'true' &&
            f.source &&
            f.source.startsWith('mapbox-gl-draw')
        );

        if (pointFeature) {
            // Hovering over point - show highlight with move cursor
            map.getCanvas().style.cursor = 'move';

            // Update feature hover highlight
            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        geometry: pointFeature.geometry,
                        properties: {}
                    }]
                });
            }
        } else {
            // Not hovering over point - reset
            map.getCanvas().style.cursor = '';

            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }
        }
    };

    // Add the handler
    map.on('mousemove', vertexHoverHandler);
}

/**
 * Set up cursor feedback when hovering over vertices in edit mode
 * Changes cursor to crosshair when hovering over a vertex to indicate it can be dragged
 */
function setupVertexCursorFeedback() {
    if (!map || !draw) return;

    // Remove previous handler if it exists
    if (vertexHoverHandler) {
        map.off('mousemove', vertexHoverHandler);
    }

    // Create new handler
    vertexHoverHandler = (e) => {
        // Only apply in direct_select mode (when editing vertices)
        if (!editMode || draw.getMode() !== 'direct_select') {
            return;
        }

        // Query features at mouse point
        const features = map.queryRenderedFeatures(e.point);

        // Find vertex or midpoint feature (only for lines/polygons)
        const vertexFeature = features.find(f => f.properties && f.properties.meta === 'vertex');
        const midpointFeature = features.find(f => f.properties && f.properties.meta === 'midpoint');
        const hoveredVertex = vertexFeature || midpointFeature;

        // Find the actual draw feature (the line/polygon/point being edited)
        const drawFeature = features.find(f =>
            f.properties &&
            f.properties.meta === 'feature' &&
            f.source &&
            f.source.startsWith('mapbox-gl-draw')
        );

        // Find point features (points don't have vertices, they are movable as a whole)
        const pointFeature = features.find(f =>
            f.geometry &&
            f.geometry.type === 'Point' &&
            f.properties &&
            f.properties.active === 'true' &&
            f.source &&
            f.source.startsWith('mapbox-gl-draw')
        );

        if (hoveredVertex) {
            // Hovering over a vertex - show vertex highlight with crosshair cursor
            map.getCanvas().style.cursor = 'crosshair';

            // Update vertex hover highlight
            const coords = hoveredVertex.geometry.coordinates;
            if (coords && (!hoveredVertexCoords ||
                hoveredVertexCoords[0] !== coords[0] ||
                hoveredVertexCoords[1] !== coords[1])) {

                hoveredVertexCoords = coords;

                const vertexSource = map.getSource('vertex-hover-source');
                if (vertexSource) {
                    vertexSource.setData({
                        type: 'FeatureCollection',
                        features: [{
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: coords
                            },
                            properties: {}
                        }]
                    });
                }
            }

            // Clear feature highlight when hovering vertex
            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }

        } else if (pointFeature || drawFeature) {
            // Hovering over feature body (line/polygon) or point - show feature highlight with move cursor
            map.getCanvas().style.cursor = 'move';

            // Clear vertex highlight
            hoveredVertexCoords = null;
            const vertexSource = map.getSource('vertex-hover-source');
            if (vertexSource) {
                vertexSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }

            // Update feature hover highlight
            const featureToHighlight = pointFeature || drawFeature;
            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        geometry: featureToHighlight.geometry,
                        properties: {}
                    }]
                });
            }

        } else {
            // Not hovering over anything - reset everything
            map.getCanvas().style.cursor = '';
            hoveredVertexCoords = null;

            const vertexSource = map.getSource('vertex-hover-source');
            if (vertexSource) {
                vertexSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }

            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }
        }
    };

    // Add the handler
    map.on('mousemove', vertexHoverHandler);
}

/**
 * Toggle edit mode on/off
 * @param {boolean} enabled - Whether to enable edit mode
 * @param {string} layerId - The layer ID to edit
 * @returns {boolean} Whether edit mode was successfully toggled
 */
/**
 * Set up hover handlers for a layer (for edit mode selection)
 * @param {string} layerId - The layer ID
 */
export function setupLayerEditHover(layerId) {
    if (!map || !map.getLayer(layerId)) return;

    // Initialize handler storage if needed
    if (!map._layerEditHoverHandlers) {
        map._layerEditHoverHandlers = {};
    }

    // Remove any existing handler for this layer first
    if (map._layerEditHoverHandlers[layerId]) {
        map.off('mousemove', map._layerEditHoverHandlers[layerId]);
        delete map._layerEditHoverHandlers[layerId];
    }

    // Set up mousemove handler to show hover highlights for features in edit mode
    const layerHoverHandler = (e) => {
        // Only show hover feedback in simple_select mode (before feature is selected)
        if (!editMode || !draw || draw.getMode() !== 'simple_select') {
            return;
        }

        // Query features at mouse point from the edit layer
        const features = map.queryRenderedFeatures(e.point, {
            layers: [layerId]
        });

        if (features.length > 0) {
            const feature = features[0];
            map.getCanvas().style.cursor = 'pointer';

            // Show feature highlight
            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        geometry: feature.geometry,
                        properties: {}
                    }]
                });
            }
        } else {
            // Clear highlight when not hovering
            map.getCanvas().style.cursor = '';

            const featureSource = map.getSource('feature-hover-source');
            if (featureSource) {
                featureSource.setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }
        }
    };

    // Add the mousemove handler
    map.on('mousemove', layerHoverHandler);

    // Store handler reference for cleanup
    map._layerEditHoverHandlers[layerId] = layerHoverHandler;
}

export function toggleEditMode(enabled, layerId = null) {
    if (!draw) return false;

    if (enabled) {
        // ========== ENTER EDIT MODE ==========
        enterEditMode(layerId);
    } else {
        // ========== EXIT EDIT MODE ==========
        exitEditMode();
    }

    return true;
}

/**
 * Enter edit mode - clean state transition
 */
function enterEditMode(layerId) {
    // Save the currently highlighted feature before clearing
    const featureToEdit = currentHighlightedFeature;

    // Set state
    editMode = true;
    currentEditLayerId = layerId;

    // Set this layer as the current layer to ensure table updates work
    import('./modules/layerManager.js').then(({ setCurrentLayer }) => {
        setCurrentLayer(layerId);
    });

    // Clean up
    removeHighlight();
    clearEditHoverSources();
    if (popup) popup.remove();

    // Add draw control to map
    try {
        map.addControl(draw, 'top-left');
    } catch (e) {
        console.warn('Could not add draw control:', e);
    }

    // Set up hover handlers
    if (layerId) {
        setupLayerEditHover(layerId);
    }

    // If there was a highlighted feature, load it for editing
    if (featureToEdit) {
        console.log('Loading previously highlighted feature for editing');
        loadSingleFeatureForEditing(featureToEdit, layerId);
    }

    // Edit controls are now in the legend, no need for popup
    // showEditControlsPopup(layerId);
}

/**
 * Exit edit mode - clean state transition
 */

function exitEditMode() {
    console.log('=== EXITING EDIT MODE ===');
    console.log('currentEditLayerId:', currentEditLayerId);

    // If actively drawing, exit drawing mode first
    if (activeDrawingMode) {
        console.log('[EXIT] Exiting drawing mode before exiting edit mode');
        stopDrawingMode();
    }

    // ======================================================
    // 1. Sync edited geometry back into full layer GeoJSON
    // ======================================================
    // Skip geometry save if we were in drawing mode (new features already saved)
    if (activeDrawingMode) {
        console.log('[EXIT] Was in drawing mode - skipping geometry save (features already saved via geometryCreated)');
    } else if (currentEditLayerId && draw && map) {
        try {
            const layer = getLayer(currentEditLayerId);
            console.log('Layer retrieved:', layer ? 'YES' : 'NO');

            const drawData = draw.getAll();
            console.log('Draw features count:', drawData?.features?.length || 0);

            if (
                layer &&
                layer.geojson &&
                Array.isArray(layer.geojson.features) &&
                drawData &&
                drawData.features.length === 1
            ) {
                const editedDrawFeature = drawData.features[0];

                // ROBUST: Use the stored feature index that we locked when loading
                const idx = currentEditFeatureIndex;

                console.log('[SAVE] Using locked feature index:', idx);
                console.log('[SAVE] Edited geometry:', editedDrawFeature.geometry);

                if (!Number.isInteger(idx)) {
                    console.error('CRITICAL: No feature index stored! Cannot save edits.');
                    // Don't return - still need to cleanup below
                } else if (!layer.geojson.features[idx]) {
                    console.error('CRITICAL: Feature at index ' + idx + ' does not exist in layer!');
                    // Don't return - still need to cleanup below
                } else {
                    // Valid index and feature exists - save the edits
                    const original = layer.geojson.features[idx];
                    console.log('[SAVE] Original feature found at index:', idx);

                    const mergedFeature = {
                        ...original,
                        geometry: JSON.parse(JSON.stringify(editedDrawFeature.geometry)),
                        properties: {
                            ...original.properties,
                            __featureIndex: idx
                        }
                    };

                    let updatedGeoJSON = {
                        ...layer.geojson,
                        features: layer.geojson.features.map((f, i) => i === idx ? mergedFeature : f)
                    };

                    updatedGeoJSON = applyFeatureIndices(updatedGeoJSON);

                    layer.geojson = updatedGeoJSON;
                    console.log('[SAVE] Layer geojson updated in memory');

                    updateLayerGeometry(currentEditLayerId, updatedGeoJSON);
                    console.log('[SAVE] Map source updated');

                    document.dispatchEvent(new CustomEvent('layerEdited', {
                        detail: { layerId: currentEditLayerId }
                    }));

                    console.log('[SAVE] Edited geometry synced correctly (saves happen via geometryUpdated events)');
                }
            } else {
                console.warn('[SAVE] WARNING: Conditions not met for saving:');
                console.warn('  - Layer exists:', !!layer);
                console.warn('  - Layer has geojson:', !!(layer && layer.geojson));
                console.warn('  - Features is array:', !!(layer && Array.isArray(layer.geojson?.features)));
                console.warn('  - Draw data exists:', !!drawData);
                console.warn('  - Draw features count:', drawData?.features?.length || 0);
            }

        } catch (e) {
            console.error('[SAVE] ERROR: Failed to sync edited geometry:', e);
        }
    } else {
        console.warn('[SAVE] WARNING: Missing required components:', {
            currentEditLayerId: !!currentEditLayerId,
            draw: !!draw,
            map: !!map
        });
    }

    // ======================================================
    // 2. Cleanup
    // ======================================================
    draw.deleteAll();

    if (vertexHoverHandler) {
        map.off('mousemove', vertexHoverHandler);
        vertexHoverHandler = null;
    }

    if (map._layerEditHoverHandlers) {
        for (const [layerId, handler] of Object.entries(map._layerEditHoverHandlers)) {
            map.off('mousemove', handler);
        }
        map._layerEditHoverHandlers = {};
    }

    clearEditHoverSources();

    if (popup) popup.remove();

    if (map && map.getCanvas()) {
        map.getCanvas().style.cursor = '';
    }

    try {
        map.removeControl(draw);
    } catch {}

    editMode = false;
    currentEditLayerId = null;
    currentEditFeatureIndex = null; // Clear the locked feature index
    activeDrawingMode = null; // Clear active drawing mode

    // Reset undo/redo history when exiting edit mode
    editHistory = [];
    editHistoryIndex = -1;
    updateUndoButtonState();

    // Edit controls are now in the legend, no need to hide popup
    // hideEditControlsPopup();

    console.log('Edit mode disabled');
}




/**
 * Clear all edit mode hover sources
 */
function clearEditHoverSources() {
    const vertexHoverSource = map.getSource('vertex-hover-source');
    if (vertexHoverSource) {
        vertexHoverSource.setData({
            type: 'FeatureCollection',
            features: []
        });
    }

    const featureHoverSource = map.getSource('feature-hover-source');
    if (featureHoverSource) {
        featureHoverSource.setData({
            type: 'FeatureCollection',
            features: []
        });
    }

    hoveredVertexCoords = null;
}

/**
 * Load a single feature into the draw control for editing
 * @param {Object} feature - The original feature from source data (not rendered)
 * @param {string} layerId - The layer ID (optional, for reference)
 */
function loadSingleFeatureForEditing(feature, layerId = null) {
    if (!map || !draw || !feature) return;

    console.log('Loading ORIGINAL feature geometry for editing');

    const newFeatureIndex = feature.properties && feature.properties.__featureIndex;

    if (!Number.isInteger(newFeatureIndex)) {
        console.error('CRITICAL: Feature does not have a valid __featureIndex!', feature);
        return;
    }

    // Don't reset history when switching features - preserve it across the entire edit session
    console.log('[EDIT] Loading feature, preserving edit history across session');

    draw.deleteAll();
    currentEditFeatureIndex = newFeatureIndex;

    console.log('[EDIT] Locked editing to feature index:', currentEditFeatureIndex);

    var featureCopy = JSON.parse(JSON.stringify(feature));

    if (!featureCopy.id) {
        featureCopy.id = 'edit-feature-' + String(currentEditFeatureIndex);
    }

    if (!featureCopy.properties) {
        featureCopy.properties = {};
    }
    featureCopy.properties.__featureIndex = currentEditFeatureIndex;

    // Log geometry details
    const geomType = featureCopy.geometry?.type;
    let vertexCount = 0;
    if (geomType === 'LineString') {
        vertexCount = featureCopy.geometry.coordinates?.length || 0;
    } else if (geomType === 'Polygon') {
        vertexCount = featureCopy.geometry.coordinates?.[0]?.length || 0;
    }
    console.log(`Geometry type: ${geomType}, Vertex count: ${vertexCount}`);

    try {
        // Add the ORIGINAL, full-resolution feature to draw control
        const featureIds = draw.add(featureCopy);

        if (featureIds && featureIds.length > 0) {
            // Add this feature's initial state to history (if not already there)
            const shouldAddToHistory = editHistory.length === 0 ||
                                      editHistory[editHistory.length - 1].properties.__featureIndex !== currentEditFeatureIndex;

            if (shouldAddToHistory) {
                const geometryCopy = JSON.parse(JSON.stringify(featureCopy.geometry));

                if (editHistory.length === 0) {
                    // First feature - initialize history
                    editHistory = [{
                        featureId: featureIds[0],
                        geometry: geometryCopy,
                        properties: { ...featureCopy.properties }
                    }];
                    editHistoryIndex = 0;
                    console.log('Initialized edit history for first feature');
                } else {
                    // Subsequent feature - remove any future history and add initial state
                    if (editHistoryIndex < editHistory.length - 1) {
                        editHistory = editHistory.slice(0, editHistoryIndex + 1);
                    }
                    editHistory.push({
                        featureId: featureIds[0],
                        geometry: geometryCopy,
                        properties: { ...featureCopy.properties }
                    });
                    editHistoryIndex = editHistory.length - 1;
                    console.log(`Added feature ${currentEditFeatureIndex} initial state to history`);
                }
                updateUndoButtonState();
            }

            // Select the feature in simple_select mode (shows vertices, but doesn't edit yet)
            // User can then click vertices or line to edit/delete
            draw.changeMode('simple_select', { featureIds: [featureIds[0]] });
            console.log('Feature loaded in simple_select mode:', featureIds[0]);

            if (geomType === 'Point') {
                // Set up point cursor feedback
                setupPointCursorFeedback();
            } else {

                // Set up vertex cursor feedback (only for line/polygon features)
                setupVertexCursorFeedback();
            }
            console.log('Draw mode is now:', draw.getMode());
            console.log('DragPan enabled?', map.dragPan ? map.dragPan.isEnabled() : 'N/A');

            // Update undo button state
            updateUndoButtonState();
        }
    } catch (error) {
        console.error('Error adding feature to draw:', error);
    }
}

/**
 * Update layer geometry after editing
 * @param {string} layerId - The layer ID
 * @param {Object} geojson - Updated GeoJSON data
 */
export function updateLayerGeometry(layerId, geojson) {
    if (!map || !mapLayers[layerId]) {
        console.warn('updateLayerGeometry: map or layer not found', { layerId, hasMap: !!map, hasLayer: !!mapLayers[layerId] });
        return;
    }

    const layerInfo = mapLayers[layerId];
    const source = map.getSource(layerInfo.sourceId);

    if (source) {
        // Ensure all features have __featureIndex
        const updatedGeojson = {
            ...geojson,
            features: (geojson.features || []).map((feature, index) => ({
                ...feature,
                properties: {
                    ...(feature.properties || {}),
                    __featureIndex: index
                }
            }))
        };

        source.setData(updatedGeojson);
        console.log(`Updated layer ${layerId} with ${updatedGeojson.features.length} features`);

        // If the map layer doesn't exist yet (e.g., layer was created with 0 features),
        // create it now based on the first feature's geometry type
        if (updatedGeojson.features.length > 0 && !map.getLayer(layerId)) {
            const firstFeature = updatedGeojson.features[0];
            const geometryType = firstFeature?.geometry?.type;
            const color = layerInfo.color || '#667eea';

            console.log(`Creating map layer ${layerId} for geometry type ${geometryType}`);

            if (geometryType === 'Point' || geometryType === 'MultiPoint') {
                map.addLayer({
                    id: layerId,
                    type: 'circle',
                    source: layerInfo.sourceId,
                    paint: {
                        'circle-radius': 6,
                        'circle-color': color,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#ffffff'
                    }
                });
            } else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
                map.addLayer({
                    id: layerId,
                    type: 'line',
                    source: layerInfo.sourceId,
                    paint: {
                        'line-color': color,
                        'line-width': 3
                    }
                });
            } else if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
                map.addLayer({
                    id: layerId,
                    type: 'fill',
                    source: layerInfo.sourceId,
                    paint: {
                        'fill-color': color,
                        'fill-opacity': 0.3,
                        'fill-outline-color': color
                    }
                });
            }

            // Store updated layer info
            if (!layerInfo.color) {
                layerInfo.color = color;
            }

            console.log(`Map layer ${layerId} created successfully`);
        }
    } else {
        console.warn('updateLayerGeometry: source not found', { layerId, sourceId: layerInfo.sourceId });
    }
}

/**
 * Get current GeoJSON data from a layer's map source
 * @param {string} layerId - The layer ID
 * @returns {Object|null} Current GeoJSON data or null
 */
export function getLayerSourceData(layerId) {
    if (!map || !mapLayers[layerId]) {
        console.error('getLayerSourceData: Invalid map or layerId', layerId);
        return null;
    }

    const layerInfo = mapLayers[layerId];
    const source = map.getSource(layerInfo.sourceId);

    if (!source) {
        console.error('getLayerSourceData: Source not found for', layerInfo.sourceId);
        return null;
    }

    // Try multiple methods to get the data
    let sourceData = null;

    // Method 1: Direct _data access (most reliable for GeoJSON sources)
    if (source._data) {
        sourceData = source._data;
    }
    // Method 2: serialize() method
    else if (typeof source.serialize === 'function') {
        const serialized = source.serialize();
        if (serialized && serialized.data) {
            sourceData = serialized.data;
        }
    }

    if (!sourceData) {
        console.error('getLayerSourceData: Could not retrieve source data');
        return null;
    }

    console.log('Retrieved source data with', sourceData.features?.length || 0, 'features');

    // Deep clone and clean the data
    const geojson = {
        type: 'FeatureCollection',
        features: (sourceData.features || []).map(feature => {
            if (!feature || !feature.geometry) {
                console.warn('Skipping feature with no geometry');
                return null;
            }

            // Clean properties - remove internal properties
            const cleanProps = {};
            if (feature.properties) {
                for (const key in feature.properties) {
                    if (!key.startsWith('__') && !key.startsWith('_') && key !== 'id') {
                        cleanProps[key] = feature.properties[key];
                    }
                }
            }

            return {
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(feature.geometry)),
                properties: cleanProps
            };
        }).filter(f => f !== null)
    };

    console.log('Cleaned GeoJSON has', geojson.features.length, 'features');

    return geojson;
}

/**
 * Check if edit mode is active
 * @returns {boolean} Whether edit mode is active
 */
export function isEditMode() {
    console.log('editMode',editMode)
    return editMode;
}

/**
 * Get the current edit layer ID
 * @returns {string|null} The current edit layer ID
 */
export function getCurrentEditLayerId() {
    return currentEditLayerId;
}

/**
 * Start drawing mode to add new features
 * @param {string} geometryType - The geometry type to draw (Point, LineString, Polygon)
 */
export function startDrawingMode(geometryType) {
    if (!draw || !editMode) {
        console.error('Cannot start drawing: edit mode not active');
        return false;
    }

    // Clear any existing selections
    draw.deleteAll();

    // Determine the drawing mode based on geometry type
    let drawMode = 'draw_polygon';
    if (geometryType === 'Point' || geometryType.includes('Point')) {
        drawMode = 'draw_point';
    } else if (geometryType === 'LineString' || geometryType.includes('Line')) {
        drawMode = 'draw_line_string';
    } else if (geometryType === 'Polygon' || geometryType.includes('Polygon')) {
        drawMode = 'draw_polygon';
    }

    // Store the active drawing mode for continuous drawing
    activeDrawingMode = drawMode;

    // Enter drawing mode
    draw.changeMode(drawMode);
    console.log(`[DRAW] Started drawing mode: ${drawMode} (continuous mode enabled)`);

    // Update cursor
    if (map && map.getCanvas()) {
        map.getCanvas().style.cursor = 'crosshair';
    }

    // Update button state
    updateDrawButtonState(true);

    return true;
}

/**
 * Stop drawing mode and return to selection mode
 */
export function stopDrawingMode() {
    if (!draw || !editMode) return false;

    // Clear any features still in draw control
    draw.deleteAll();

    // Clear the active drawing mode
    activeDrawingMode = null;

    // Return to simple select mode
    draw.changeMode('simple_select');

    // Reset cursor
    if (map && map.getCanvas()) {
        map.getCanvas().style.cursor = '';
    }

    // Update button state
    updateDrawButtonState(false);

    console.log('[DRAW] Stopped drawing mode');
    return true;
}

/**
 * Save all drawing changes - commit features from draw control to layer
 */
export function saveDrawingChanges(layerId) {
    if (!draw || !editMode) return false;

    // Get all features from draw control
    const allFeatures = draw.getAll();

    if (!allFeatures || !allFeatures.features || allFeatures.features.length === 0) {
        console.log('[SAVE] No features to save');
        stopDrawingMode();

        // Reset draw button
        const legendDrawBtn = document.getElementById('legend-draw-btn');
        if (legendDrawBtn) {
            legendDrawBtn.style.background = '';
            legendDrawBtn.style.borderColor = '';
            legendDrawBtn.style.color = '';
        }
        return true;
    }

    console.log(`[SAVE] Saving ${allFeatures.features.length} feature(s) to layer`);

    // Dispatch events to save each feature to the layer
    allFeatures.features.forEach(feature => {
        const event = new CustomEvent('geometryCreated', {
            detail: {
                layerId: layerId,
                geometry: feature.geometry,
                properties: feature.properties || {}
            }
        });
        document.dispatchEvent(event);
    });

    // Clear draw control
    draw.deleteAll();

    // Stop drawing mode
    activeDrawingMode = null;
    draw.changeMode('simple_select');

    // Reset cursor
    if (map && map.getCanvas()) {
        map.getCanvas().style.cursor = '';
    }

    // Reset draw button
    const legendDrawBtn = document.getElementById('legend-draw-btn');
    if (legendDrawBtn) {
        legendDrawBtn.style.background = '';
        legendDrawBtn.style.borderColor = '';
        legendDrawBtn.style.color = '';
    }

    console.log('[SAVE] Features saved successfully');

    // Show success message
    const showStatus = window.showStatus || ((msg) => console.log(msg));
    showStatus(`Saved ${allFeatures.features.length} feature(s)`, 'success');

    return true;
}

/**
 * Undo the last geometry edit
 */
export function undoEdit() {
    if (!draw || !editMode || editHistory.length === 0) return false;

    // Can't undo if we're at the first state
    if (editHistoryIndex <= 0) {
        console.log('Nothing to undo');
        return false;
    }

    const currentState = editHistory[editHistoryIndex];

    // Move back in history
    editHistoryIndex--;
    const previousState = editHistory[editHistoryIndex];

    console.log(`Undoing to state ${editHistoryIndex + 1}/${editHistory.length}`);

    isUndoRedoAction = true;

    try {
        // If current state is a deletion, re-add the feature
        if (currentState.action === 'delete') {
            console.log('Undoing deletion - re-adding feature');

            const featureToRestore = {
                id: previousState.featureId,
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(previousState.geometry)),
                properties: { ...previousState.properties }
            };

            draw.deleteAll();
            draw.add(featureToRestore);

            const geomType = featureToRestore.geometry?.type;
            if (geomType === 'Point') {
                draw.changeMode('simple_select', { featureIds: [previousState.featureId] });
                setupPointCursorFeedback();
            } else {
                draw.changeMode('direct_select', { featureId: previousState.featureId });
                setupVertexCursorFeedback();
            }

            // Only dispatch event for committed features (those with __featureIndex)
            if (featureToRestore.properties && featureToRestore.properties.__featureIndex !== undefined) {
                const event = new CustomEvent('geometryUpdated', {
                    detail: {
                        featureIndex: featureToRestore.properties.__featureIndex,
                        geometry: featureToRestore.geometry
                    }
                });
                document.dispatchEvent(event);
            }
        } else {
            // Normal undo - restore previous geometry
            // Clear draw control and recreate feature with previous state
            draw.deleteAll();

            const featureToRestore = {
                id: previousState.featureId,
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(previousState.geometry)),
                properties: { ...previousState.properties }
            };

            draw.add(featureToRestore);

            const geomType = featureToRestore.geometry?.type;
            if (geomType === 'Point') {
                draw.changeMode('simple_select', { featureIds: [previousState.featureId] });
                setupPointCursorFeedback();
            } else {
                draw.changeMode('direct_select', { featureId: previousState.featureId });
                setupVertexCursorFeedback();
            }

            // Only dispatch event for committed features (those with __featureIndex)
            if (featureToRestore.properties && featureToRestore.properties.__featureIndex !== undefined) {
                const event = new CustomEvent('geometryUpdated', {
                    detail: {
                        featureIndex: featureToRestore.properties.__featureIndex,
                        geometry: featureToRestore.geometry
                    }
                });
                document.dispatchEvent(event);
            }
        }

        updateUndoButtonState();
        return true;
    } finally {
        isUndoRedoAction = false;
    }
}

export function redoEdit() {
    if (!draw || !editMode || editHistory.length === 0) return false;

    if (editHistoryIndex >= editHistory.length - 1) {
        console.log('Nothing to redo');
        return false;
    }

    editHistoryIndex++;
    const nextState = editHistory[editHistoryIndex];

    console.log(`Redoing to state ${editHistoryIndex + 1}/${editHistory.length}`);

    isUndoRedoAction = true;

    try {
        // If next state is a deletion, delete the feature
        if (nextState.action === 'delete') {
            console.log('Redoing deletion');
            draw.delete([nextState.featureId]);

            // Only dispatch event for committed features (those with __featureIndex)
            if (nextState.properties && nextState.properties.__featureIndex !== undefined) {
                const event = new CustomEvent('geometryDeleted', {
                    detail: {
                        featureIndex: nextState.properties.__featureIndex
                    }
                });
                document.dispatchEvent(event);
            }
        } else {
            // Normal redo - restore next geometry
            // Clear draw control and recreate feature with next state
            draw.deleteAll();

            const featureToRestore = {
                id: nextState.featureId,
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(nextState.geometry)),
                properties: { ...nextState.properties }
            };

            draw.add(featureToRestore);

            const geomType = featureToRestore.geometry?.type;
            if (geomType === 'Point') {
                draw.changeMode('simple_select', { featureIds: [nextState.featureId] });
                setupPointCursorFeedback();
            } else {
                draw.changeMode('direct_select', { featureId: nextState.featureId });
                setupVertexCursorFeedback();
            }

            // Only dispatch event for committed features (those with __featureIndex)
            if (featureToRestore.properties && featureToRestore.properties.__featureIndex !== undefined) {
                const event = new CustomEvent('geometryUpdated', {
                    detail: {
                        featureIndex: featureToRestore.properties.__featureIndex,
                        geometry: featureToRestore.geometry
                    }
                });
                document.dispatchEvent(event);
            }
        }

        updateUndoButtonState();
        return true;
    } finally {
        isUndoRedoAction = false;
    }
}


function updateUndoButtonState() {
    const event = new CustomEvent('editHistoryChanged', {
        detail: {
            canUndo: editHistoryIndex > 0,
            canRedo: editHistoryIndex < editHistory.length - 1,
            currentIndex: editHistoryIndex,
            totalStates: editHistory.length
        }
    });
    document.dispatchEvent(event);
}

/**
 * Reset edit history after saving changes
 * This clears the undo/redo stack since changes have been persisted
 */
export function resetEditHistory() {
    if (!editMode || !draw) return;

    const allFeatures = draw.getAll();
    if (allFeatures && allFeatures.features && allFeatures.features.length > 0) {
        const currentFeature = allFeatures.features[0];

        // Reset history with just the current saved state
        editHistory = [{
            featureId: currentFeature.id,
            geometry: JSON.parse(JSON.stringify(currentFeature.geometry)),
            properties: { ...currentFeature.properties }
        }];
        editHistoryIndex = 0;
    } else {
        // No features in draw control, clear history completely
        editHistory = [];
        editHistoryIndex = -1;
    }

    // Update button states to reflect the reset
    updateUndoButtonState();
    console.log('Edit history reset after save');
}

/**
 * Get the first feature layer ID to insert highlights underneath
 */
function getFirstFeatureLayerId() {
    const layers = map.getStyle().layers;
    for (const layer of layers) {
        if (layer.id && (layer.id.includes('-source') || layer.source && !layer.source.startsWith('mapbox-gl-draw'))) {
            return layer.id;
        }
    }
    return undefined;
}

export function highlightFeature(feature, shouldZoom = true) {
    console.log('highlightFeature called, editMode:', editMode);
    if (!map || !feature) return;

    if (editMode) return;

    removeHighlight();

    // Store the currently highlighted feature
    currentHighlightedFeature = feature;

    const highlightSourceId = 'highlight-source';
    const highlightLayerId = 'highlight-layer';
    const highlightOutlineId = 'highlight-layer-outline';

    try {
        if (map.getSource(highlightSourceId)) {
            console.warn('Highlight source already exists, removing it first');
            removeHighlight();
        }

        map.addSource(highlightSourceId, {
            type: 'geojson',
            data: feature
        });

        const geomType = feature.geometry.type;

        if (geomType === 'Point' || geomType === 'MultiPoint') {
            map.addLayer({
                id: highlightLayerId,
                type: 'circle',
                source: highlightSourceId,
                paint: {
                    'circle-radius': 22,
                    'circle-color': 'rgba(75, 192, 192, 1)',
                    'circle-opacity': 0.5,
                    'circle-blur': 1.5
                }
            });
        } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
            map.addLayer({
                id: highlightLayerId,
                type: 'line',
                source: highlightSourceId,
                paint: {
                    'line-color': 'rgba(75, 192, 192, 1)',
                    'line-width': 12,
                    'line-opacity': 0.5,
                    'line-blur': 3
                }
            });
        } else {
            map.addLayer({
                id: highlightLayerId,
                type: 'fill',
                source: highlightSourceId,
                paint: {
                    'fill-color': 'rgba(75, 192, 192, 1)',
                    'fill-opacity': 0.25
                }
            });

            map.addLayer({
                id: highlightOutlineId,
                type: 'line',
                source: highlightSourceId,
                paint: {
                    'line-color': 'rgba(75, 192, 192, 1)',
                    'line-width': 12,
                    'line-opacity': 0.6,
                    'line-blur': 3
                }
            });
        }

        if (shouldZoom) {
            zoomToFeature(feature);
        }

        highlightLayer = highlightLayerId;
    } catch (error) {
        console.error('Error highlighting feature:', error);
    }
}

export function removeHighlight() {
    if (!map) return;

    const layersToRemove = ['highlight-layer', 'highlight-layer-outline'];

    layersToRemove.forEach(layerId => {
        try {
            if (map.getLayer(layerId)) {
                map.removeLayer(layerId);
            }
        } catch (error) {
            console.warn(`Failed to remove layer ${layerId}:`, error);
        }
    });

    try {
        if (map.getSource('highlight-source')) {
            map.removeSource('highlight-source');
        }
    } catch (error) {
        console.warn('Failed to remove highlight source:', error);
    }

    highlightLayer = null;
    currentHighlightedFeature = null; // Clear tracked feature
}

function zoomToFeature(feature, duration = 2500) {
    if (!feature || !feature.geometry) return;

    const coords = [];
    extractAllCoordinates(feature.geometry, coords);

    if (coords.length === 0) return;

    // Calculate bounds
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    coords.forEach(coord => {
        if (coord && coord.length >= 2) {
            const lng = coord[0];
            const lat = coord[1];
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    });

    const width = maxLng - minLng;
    const height = maxLat - minLat;
    const isPoint = width < 0.0001 && height < 0.0001;

    if (isPoint) {
        map.flyTo({
            center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
            zoom: 13,
            duration: duration
        });
    } else {
        map.fitBounds([
            [minLng, minLat],
            [maxLng, maxLat]
        ], {
            padding: 50,
            maxZoom: 16,
            duration: duration
        });
    }
}

function applyFeatureIndices(geojson) {
    if (!geojson || !Array.isArray(geojson.features)) return geojson;

    return {
        ...geojson,
        features: geojson.features.map((f, i) => ({
            ...f,
            properties: {
                ...(f.properties || {}),
                __featureIndex: i
            }
        }))
    };
}

/**
 * Get the first user layer ID (for inserting base map underneath)
 */
function getFirstUserLayerId() {
    const layers = map.getStyle().layers;
    for (const layer of layers) {
        // Find the first layer that's not a mapbox-gl-draw layer or base map
        if (layer.id &&
            !layer.id.startsWith('gl-draw') &&
            !layer.id.includes('osm') &&
            !layer.id.includes('carto') &&
            !layer.id.includes('esri') &&
            !layer.id.includes('highlight') &&
            !layer.id.includes('vertex') &&
            !layer.id.includes('feature-hover')) {
            return layer.id;
        }
    }
    return undefined;
}

/**
 * Toggle dark map style
 */
export function toggleDarkMap() {
    if (!map) return false;

    const currentStyle = map.getStyle();
    const isDark = currentStyle.sources['carto-dark'] || currentStyle.sources['esri-dark'];

    // Remove existing tile sources and layers
    const tileSources = ['osm', 'carto-dark', 'esri-dark'];
    const tileLayers = ['osm', 'carto-dark', 'esri-dark'];

    tileLayers.forEach(layerId => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
    });

    tileSources.forEach(sourceId => {
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
    });

    // Add new style
    if (isDark) {
        // Switch to light (OSM)
        map.addSource('osm', {
            type: 'raster',
            tiles: [
                'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        });

        map.addLayer({
            id: 'osm',
            type: 'raster',
            source: 'osm',
            minzoom: 0,
            maxzoom: 19
        }, getFirstUserLayerId());

        return false; // Return false = light mode
    } else {
        // Switch to dark (Carto Dark Matter)
        map.addSource('carto-dark', {
            type: 'raster',
            tiles: [
                'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        });

        map.addLayer({
            id: 'carto-dark',
            type: 'raster',
            source: 'carto-dark',
            minzoom: 0,
            maxzoom: 19
        }, getFirstUserLayerId());

        return true; // Return true = dark mode
    }
}

/**
 * Show edit controls popup at top of map
 * @param {string} layerId - The layer being edited
 */
function showEditControlsPopup(layerId) {
    const popup = document.getElementById('edit-controls-popup');
    const layerNameEl = document.getElementById('edit-layer-name');

    if (popup && layerNameEl) {
        // Get layer name from layerManager
        const layer = getLayer(layerId);
        if (layer) {
            layerNameEl.textContent = `Editing: ${layer.name || 'Layer'}`;
        }

        popup.style.display = 'block';

        // Update undo/redo button states
        updateEditControlButtons();

        // Make popup draggable (initialize once)
        if (!popup.dataset.draggableInitialized) {
            makePopupDraggable(popup);
            popup.dataset.draggableInitialized = 'true';
        }
    }
}

/**
 * Make the edit controls popup draggable
 * @param {HTMLElement} popup - The popup element
 */
function makePopupDraggable(popup) {
    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;
    let hasBeenDragged = false;

    const header = popup.querySelector('.edit-controls-header');

    header.addEventListener('mousedown', dragStart);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', dragEnd);

    function dragStart(e) {
        // Don't drag if clicking the close button
        if (e.target.closest('.edit-close-btn')) return;

        isDragging = true;

        // Get the popup's current position on screen
        const rect = popup.getBoundingClientRect();

        // Calculate offset between mouse position and popup's top-left corner
        offsetX = e.clientX - rect.left;
        offsetY = e.clientY - rect.top;

        // On first drag, convert from centered CSS positioning to absolute
        if (!hasBeenDragged) {
            popup.style.left = rect.left + 'px';
            popup.style.top = rect.top + 'px';
            popup.style.transform = 'none';
            hasBeenDragged = true;
        }
    }

    function drag(e) {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position based on mouse and offset
        const newX = e.clientX - offsetX;
        const newY = e.clientY - offsetY;

        // Update position
        popup.style.left = newX + 'px';
        popup.style.top = newY + 'px';
    }

    function dragEnd() {
        isDragging = false;
    }
}

/**
 * Hide edit controls popup
 */
function hideEditControlsPopup() {
    const popup = document.getElementById('edit-controls-popup');
    if (popup) {
        popup.style.display = 'none';
    }
}

/**
 * Update undo/redo/save button states in edit controls popup
 */
export function updateEditControlButtons() {
    const undoBtn = document.getElementById('edit-undo-btn');
    const redoBtn = document.getElementById('edit-redo-btn');
    const saveBtn = document.getElementById('edit-save-btn');

    // Also get legend buttons
    const legendUndoBtn = document.getElementById('legend-undo-btn');
    const legendRedoBtn = document.getElementById('legend-redo-btn');

    // Check if undo/redo are available
    const canUndoEdit = editHistoryIndex > 0;
    const canRedoEdit = editHistoryIndex < editHistory.length - 1;

    // Update popup buttons
    if (undoBtn && redoBtn && saveBtn) {
        undoBtn.disabled = !canUndoEdit;
        redoBtn.disabled = !canRedoEdit;

        // Save button is enabled only if changes have been made
        // (editHistoryIndex > 0 means we've moved away from the initial state)
        saveBtn.disabled = editHistoryIndex <= 0;
    }

    // Update legend buttons
    if (legendUndoBtn) {
        legendUndoBtn.disabled = !canUndoEdit;
    }
    if (legendRedoBtn) {
        legendRedoBtn.disabled = !canRedoEdit;
    }
}
